"use client";

import { useEffect, useRef } from "react";
import { useSession } from "next-auth/react";
import { getConsent, onConsentChange } from "@/lib/consent";
import { isProductionClient } from "@/lib/site-env";
import { KLAVIYO_ONSITE_SCRIPT_ID, klaviyoOnsiteScriptUrl } from "@/lib/klaviyo-config";

declare global {
  interface Window {
    klaviyo?: { identify: (attrs: Record<string, unknown>) => unknown };
    _klOnsite?: unknown[];
  }
}

/**
 * Klaviyo onsite tracking for the public site.
 *
 * Until this existed the site only ever talked to Klaviyo from the server —
 * form submissions became profiles and "Submitted Quote" events, and that was
 * all Klaviyo knew. It had no idea who was on the site: no "Active on Site",
 * no page views, no way to link an email click to what the visitor did next,
 * and no way to show a Klaviyo form. The portal has had this script since May;
 * the site never got it, and "Active on Site" has read zero for the account's
 * whole life.
 *
 * Two gates, same as every other tracker here:
 *  - production host only (site-env.ts) — the company ID is public, so this is
 *    the only thing keeping dev traffic out of the real account;
 *  - cookie consent — Klaviyo sets its own identifying cookie, so the script
 *    is not loaded at all until the visitor accepts, and loads the moment they
 *    do. A rejecting visitor never fetches it.
 *
 * Once loaded, a signed-in customer is identified by the email on their
 * session so their browsing lands on the profile the shop already has, rather
 * than on an anonymous one that only merges if they click a Klaviyo email.
 */
export default function KlaviyoTracker() {
  const { data: session } = useSession();
  const email = session?.user?.email?.toLowerCase() ?? null;
  const identified = useRef<string | null>(null);

  useEffect(() => {
    if (!isProductionClient()) return;

    const load = () => {
      if (document.getElementById(KLAVIYO_ONSITE_SCRIPT_ID)) return;
      const s = document.createElement("script");
      s.id = KLAVIYO_ONSITE_SCRIPT_ID;
      s.async = true;
      s.src = klaviyoOnsiteScriptUrl();
      document.head.appendChild(s);
    };

    if (getConsent() === "granted") load();
    return onConsentChange((value) => {
      if (value === "granted") load();
    });
  }, []);

  useEffect(() => {
    if (!email || identified.current === email) return;
    if (!isProductionClient() || getConsent() !== "granted") return;
    // klaviyo.js replaces window.klaviyo with the real object once it loads;
    // before that the queue below holds the call. Either way it is delivered.
    const kl = window.klaviyo;
    if (kl && typeof kl.identify === "function") {
      void kl.identify({ email });
    } else {
      (window._klOnsite = window._klOnsite || []).push(["identify", { email }]);
    }
    identified.current = email;
  }, [email]);

  return null;
}
